import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { MapContainer, TileLayer, CircleMarker } from "react-leaflet";
import {
  X,
  VideoCamera,
  ArrowSquareOut,
  MapPin,
  Info,
  Compass,
} from "@phosphor-icons/react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * CameraViewerModal — in-app modal that opens a wildfire camera.
 *
 * WA DNR's viewer (wadnr.wildfirewatch.com) sets CSP `frame-ancestors 'none'`,
 * so the live feed cannot be embedded directly. We open the viewer in a
 * chromeless popup window via window.open(...) with sized popup features,
 * which keeps the experience tied to this app while still respecting the
 * upstream's security policy.
 */
export default function CameraViewerModal({ camera, onClose }) {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  if (!camera) return null;

  const embeddable = camera.embeddable === true;
  const imageInline = camera.image_inline === true;

  const openLive = () => {
    const w = 1280;
    const h = 800;
    const left = Math.max(0, (window.screen.width - w) / 2);
    const top = Math.max(0, (window.screen.height - h) / 2);
    window.open(
      camera.viewer_url,
      `firewatch-cam-${camera.id}`,
      `popup=yes,width=${w},height=${h},left=${left},top=${top},noopener,noreferrer`
    );
  };

  const mapsUrl = `https://www.google.com/maps?q=${camera.lat},${camera.lon}`;

  const modal = (
    <div
      className="fixed inset-0 z-[2100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-fade-in"
      onClick={onClose}
      data-testid="camera-viewer-modal"
    >
      <div
        className="w-full max-w-3xl bg-tac-bg border border-tac-border rounded-sm shadow-2xl max-h-[92vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-tac-border">
          <div className="flex items-start gap-3 min-w-0">
            <div className="w-10 h-10 shrink-0 bg-tac-surface border border-tac-border rounded-sm flex items-center justify-center">
              <VideoCamera size={20} weight="duotone" className="text-tac-primary" />
            </div>
            <div className="min-w-0">
              <div className="text-[15px] uppercase tracking-[0.15em] font-bold text-tac-faint">
                {camera.operator}
              </div>
              <h2
                data-testid="camera-modal-title"
                className="font-display font-black text-lg text-tac-text leading-tight"
              >
                {camera.name}
              </h2>
              {camera.county && (
                <div className="text-[14px] text-tac-dim mt-0.5 capitalize">
                  {camera.county.replace("_", " ")} County
                  {camera.distance_mi != null && (
                    <span className="text-tac-primary"> · {camera.distance_mi} mi away</span>
                  )}
                </div>
              )}
            </div>
          </div>
          <button
            data-testid="camera-modal-close"
            onClick={onClose}
            className="text-tac-dim hover:text-tac-text shrink-0 p-1"
            aria-label="Close"
          >
            <X size={20} weight="bold" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto tac-scroll">
          {/* Live action area */}
          <div className="p-5 border-b border-tac-border">
            {imageInline ? (
              <InlineCameraImage camera={camera} onOpenLive={openLive} />
            ) : embeddable ? (
              <>
                <div
                  data-testid="camera-embedded-iframe"
                  className="relative w-full bg-black border border-tac-border rounded-sm overflow-hidden"
                  style={{ aspectRatio: "16 / 9" }}
                >
                  <iframe
                    src={camera.viewer_url}
                    title={`${camera.name} live feed`}
                    className="absolute inset-0 w-full h-full"
                    referrerPolicy="no-referrer"
                    allow="fullscreen"
                  />
                </div>
                <button
                  data-testid="camera-open-live-btn"
                  onClick={openLive}
                  className="w-full mt-3 flex items-center justify-center gap-2 px-4 py-2 border border-tac-border bg-tac-surface hover:bg-tac-surface2 text-tac-text font-bold uppercase tracking-wider text-[11px] rounded-sm transition-colors"
                >
                  <ArrowSquareOut size={12} weight="bold" />
                  Open in Larger Window
                </button>
              </>
            ) : (
              <>
                <button
                  data-testid="camera-open-live-btn"
                  onClick={openLive}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-tac-primary hover:bg-tac-primary/90 text-white font-bold uppercase tracking-wider text-xs rounded-sm transition-colors active:scale-[0.99]"
                >
                  <VideoCamera size={16} weight="fill" />
                  Open Live Feed
                  <ArrowSquareOut size={12} weight="bold" />
                </button>
                <div className="mt-2.5 flex items-start gap-2 text-[14px] text-tac-faint">
                  <Info size={12} weight="bold" className="shrink-0 mt-0.5" />
                  <span>
                    Opens in a focused popup window. WA DNR's viewer requires its
                    own window for security (CSP <code className="font-mono-tac">frame-ancestors 'none'</code>).
                  </span>
                </div>
              </>
            )}
          </div>

          {/* Description */}
          {camera.description && (
            <div className="px-5 py-4 border-b border-tac-border">
              <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-tac-faint mb-1">
                Coverage
              </div>
              <p className="text-sm text-tac-text leading-relaxed">{camera.description}</p>
            </div>
          )}

          {/* Mini map */}
          <div className="px-5 py-4 border-b border-tac-border">
            <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-tac-faint mb-2 flex items-center gap-1">
              <Compass size={11} weight="bold" /> Mount Location
            </div>
            <div
              className="border border-tac-border rounded-sm overflow-hidden h-48"
              data-testid="camera-mini-map"
            >
              <MapContainer
                center={[camera.lat, camera.lon]}
                zoom={10}
                scrollWheelZoom={false}
                zoomControl={false}
                className="h-full w-full"
                style={{ background: "#0F1311" }}
              >
                <TileLayer
                  url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Topo_Map/MapServer/tile/{z}/{y}/{x}"
                  attribution="Tiles &copy; Esri"
                />
                <CircleMarker
                  center={[camera.lat, camera.lon]}
                  radius={8}
                  pathOptions={{
                    color: "#0A0D0C",
                    weight: 2,
                    fillColor: "#F2542D",
                    fillOpacity: 0.95,
                  }}
                />
              </MapContainer>
            </div>
            <div className="mt-2 flex items-center justify-between text-[15px] font-mono-tac tabular text-tac-faint">
              <span className="flex items-center gap-1">
                <MapPin size={9} weight="fill" />
                {camera.lat.toFixed(4)}°, {camera.lon.toFixed(4)}°
              </span>
              <a
                href={mapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-tac-primary hover:underline uppercase tracking-wider font-bold"
                data-testid="camera-open-in-maps"
              >
                Open in Maps ↗
              </a>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-tac-border text-[15px] font-mono-tac text-tac-faint">
          Source: {camera.operator}. Coordinates approximate (~1km).
        </div>
      </div>
    </div>
  );

  return typeof document !== "undefined" ? createPortal(modal, document.body) : modal;
}

function InlineCameraImage({ camera, onOpenLive }) {
  const [ts, setTs] = useState(() => Date.now());
  const [errored, setErrored] = useState(false);
  const src = `${BACKEND_URL}/api/cameras/${camera.id}/image.jpg?t=${ts}`;

  // Auto-refresh every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setErrored(false);
      setTs(Date.now());
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  return (
    <>
      <div
        data-testid="camera-inline-image-wrap"
        className="relative w-full bg-black border border-tac-border rounded-sm overflow-hidden"
        style={{ aspectRatio: "16 / 9" }}
      >
        {!errored && (
          <img
            data-testid="camera-inline-image"
            src={src}
            alt={`${camera.name} latest still frame`}
            className="absolute inset-0 w-full h-full object-cover"
            onError={() => setErrored(true)}
          />
        )}
        {errored && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-tac-faint text-[15px] font-mono-tac uppercase tracking-wider">
            <Info size={20} />
            <span>Camera image temporarily unavailable</span>
            <button
              onClick={() => {
                setErrored(false);
                setTs(Date.now());
              }}
              className="mt-1 px-3 py-1 border border-tac-border rounded-sm hover:border-tac-primary text-tac-text"
            >
              Retry
            </button>
          </div>
        )}
        <div className="absolute bottom-1.5 left-1.5 flex items-center gap-1 bg-black/70 px-2 py-0.5 rounded-sm">
          <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
          <span className="text-[9px] font-mono-tac uppercase tracking-wider text-white">
            Refreshing 30s
          </span>
        </div>
        <div className="absolute top-1.5 right-1.5 bg-black/70 px-2 py-0.5 rounded-sm text-[9px] font-mono-tac uppercase tracking-wider text-tac-primary">
          {camera.operator}
        </div>
      </div>
      <button
        data-testid="camera-open-live-btn"
        onClick={onOpenLive}
        className="w-full mt-3 flex items-center justify-center gap-2 px-4 py-2 border border-tac-border bg-tac-surface hover:bg-tac-surface2 text-tac-text font-bold uppercase tracking-wider text-[11px] rounded-sm transition-colors"
      >
        <ArrowSquareOut size={12} weight="bold" />
        Open Live PTZ Viewer
      </button>
      <div className="mt-2.5 flex items-start gap-2 text-[15px] text-tac-faint">
        <Info size={11} weight="bold" className="shrink-0 mt-0.5" />
        <span>
          Still image auto-refreshes every 30 s. For real-time pan/tilt/zoom control,
          open the official ALERTWildfire viewer.
        </span>
      </div>
    </>
  );
}
